/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import SendDocEmailModal from "@/components/SendDocEmailModal";
import {
  Plus, FileDown, FileSpreadsheet, Trash2, Loader2, X, Lock, Mail,
  Clock, ClipboardCheck, PackageMinus, Wrench, ArrowRight,
} from "lucide-react";

export const DOC_TYPES = [
  { id: "pontaj", icon: Clock },
  { id: "proces_verbal", icon: ClipboardCheck },
  { id: "bon_consum", icon: PackageMinus },
  { id: "fisa_lucru", icon: Wrench },
];

export default function DocumentsCenter() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  const hasAccess = user?.is_admin || ["pro", "business"].includes(user?.plan);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };

  const load = async () => {
    try { const r = await api.get("/documents"); setDocs(r.data); }
    catch { /* gated */ } finally { setLoading(false); }
  };
  useEffect(() => { if (hasAccess) load(); else setLoading(false); }, []);

  const download = async (doc, fmt) => {
    setBusyId(doc.id + fmt);
    try {
      const r = await api.get(`/documents/${doc.id}/export?fmt=${fmt}&lang=${i18n.language}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(doc.title || "document").replace(/\s+/g, "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("docs.exportError")); } finally { setBusyId(null); }
  };

  const del = async (doc) => {
    if (!window.confirm(t("docs.confirmDelete", { title: doc.title }))) return;
    try { await api.delete(`/documents/${doc.id}`); toast.success(t("docs.deleted")); setDocs((d) => d.filter((x) => x.id !== doc.id)); }
    catch { toast.error(t("calendar.error")); }
  };

  const typeMeta = (id) => DOC_TYPES.find((d) => d.id === id) || DOC_TYPES[0];

  if (!hasAccess) {
    return (
      <div className="p-5 lg:p-10 max-w-3xl mx-auto" data-testid="documents-locked">
        <div className="overline text-[#00BFFF]">{t("docs.overline")}</div>
        <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{t("docs.pageTitle")}</h1>
        <div className="card-flat p-8 mt-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#111C30] flex items-center justify-center mx-auto mb-4">
            <Lock size={26} className="text-[#00BFFF]" />
          </div>
          <h2 className="font-display font-bold text-xl">{t("docs.lockedTitle")}</h2>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-md mx-auto">{t("docs.lockedDesc")}</p>
          <button onClick={() => nav("/app/abonament")} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="upgrade-btn">
            {t("docs.seePlans")} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 lg:p-10 max-w-5xl mx-auto" data-testid="documents-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#00BFFF]">{t("docs.overline")}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{t("docs.pageTitle")}</h1>
          <p className="text-sm text-[#94A3B8] mt-2">{t("docs.pageSubtitle")}</p>
        </div>
        <button onClick={() => setPicker(true)} className="btn-primary flex items-center gap-2" data-testid="new-document-btn">
          <Plus size={16} /> {t("docs.newDocument")}
        </button>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#00BFFF]" /></div>
      ) : docs.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-[#94A3B8] mt-8" data-testid="documents-empty">
          {t("docs.empty")}
        </div>
      ) : (
        <div className="mt-8 grid gap-3">
          {docs.map((doc) => {
            const m = typeMeta(doc.doc_type);
            return (
              <div key={doc.id} className="card-flat p-4 flex items-center gap-4" data-testid={`doc-row-${doc.id}`}>
                <div className="w-11 h-11 rounded-xl bg-[#111C30] flex items-center justify-center shrink-0">
                  <m.icon size={20} className="text-[#00BFFF]" />
                </div>
                <button onClick={() => nav(`/app/documente/${doc.id}`)} className="flex-1 min-w-0 text-left" data-testid={`doc-open-${doc.id}`}>
                  <div className="font-semibold text-sm truncate">{doc.title}</div>
                  <div className="text-xs text-[#94A3B8]">{t(`docs.type_${doc.doc_type}`)} · {t("docs.updatedAt", { date: fmtDate(doc.updated_at) })}</div>
                </button>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => download(doc, "pdf")} disabled={busyId === doc.id + "pdf"} className="btn-ink p-2" title="PDF" data-testid={`doc-pdf-${doc.id}`}>
                    {busyId === doc.id + "pdf" ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                  </button>
                  <button onClick={() => download(doc, "xlsx")} disabled={busyId === doc.id + "xlsx"} className="btn-ink p-2" title="Excel" data-testid={`doc-excel-${doc.id}`}>
                    {busyId === doc.id + "xlsx" ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
                  </button>
                  <button onClick={() => setEmailDoc(doc)} className="btn-ink p-2" title={t("docs.email")} data-testid={`doc-email-${doc.id}`}>
                    <Mail size={16} />
                  </button>
                  <button onClick={() => del(doc)} className="btn-ink p-2 text-[#EF4444]" title={t("docs.deleted")} data-testid={`doc-del-${doc.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {picker && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setPicker(false)} data-testid="type-picker">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0B111D] border border-[#1E293B] w-full max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#1E293B]">
              <div className="font-display font-bold text-lg">{t("docs.pickTitle")}</div>
              <button onClick={() => setPicker(false)} className="text-[#94A3B8] hover:text-[#E2E8F0]"><X size={18} /></button>
            </div>
            <div className="p-4 grid sm:grid-cols-2 gap-3">
              {DOC_TYPES.map((ty) => (
                <button key={ty.id} onClick={() => nav(`/app/documente/nou/${ty.id}`)} className="card-flat p-4 text-left hover:border-[#00BFFF] transition-colors" data-testid={`pick-${ty.id}`}>
                  <ty.icon size={22} className="text-[#00BFFF]" />
                  <div className="font-semibold text-sm mt-2">{t(`docs.type_${ty.id}`)}</div>
                  <div className="text-xs text-[#94A3B8] mt-1">{t(`docs.type_${ty.id}_desc`)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {emailDoc && <SendDocEmailModal doc={emailDoc} onClose={() => setEmailDoc(null)} />}
    </div>
  );
}
